import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDays, formatMinutes, formatMoney, formatMonth, formatNumber } from '../lib/format';
import { excuseReasonOk, percentAmount, percentOk, type ShortfallDecision, type ShortfallDetail } from '../lib/shortfall';
import { FormPage } from '../components/FormPage';
import { Checkbox, Detail, Field, Input, Textarea, useToast } from '../components/ui';
import { ShortfallDecisionText } from './ShortDays';
import { formatWorkDate } from './Violations';

const PRESETS = [25, 50, 100, 150, 200];

/** The day, as both forms show it above their fields. */
function DaySummary({ row }: { row: ShortfallDetail }) {
  const { t, locale, localized } = useI18n();
  return (
    <dl className="divide-y divide-[var(--line)]">
      <Detail label={t('rewards.employee')}>{localized(row.user)}</Detail>
      <Detail label={t('shortfall.workDate')}>
        <span className="tabular">{formatWorkDate(row.workDate, locale)}</span>
      </Detail>
      <Detail label={t('shortfall.worked')}>
        <span className="tabular">
          {t('violations.shortDayWorked', {
            worked: formatMinutes(row.workedMinutes, locale),
            owed: formatMinutes(row.requiredMinutes, locale),
          })}
        </span>
      </Detail>
      <Detail label={t('shortfall.missing')}>
        <span className="tabular font-medium text-error-fg dark:text-error-dark">{formatMinutes(row.shortfallMinutes, locale)}</span>
      </Detail>
    </dl>
  );
}

/** One of the two decisions, as a card the administrator picks — the radio is the card. */
function Choice({
  checked,
  disabled,
  onSelect,
  title,
  hint,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  title: string;
  hint: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'rounded-lg border p-4 transition-colors duration-fast',
        checked ? 'border-primary bg-primary/5' : 'hairline',
        disabled && 'opacity-70',
      )}
    >
      <label className={clsx('flex items-start gap-3', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
        <input
          type="radio"
          name="decision"
          aria-label={title}
          className="mt-1 h-4 w-4 accent-primary"
          checked={checked}
          disabled={disabled}
          onChange={onSelect}
        />
        <span className="min-w-0">
          <span className="block text-body font-medium text-[var(--text-primary)]">{title}</span>
          <span className="block text-caption text-muted">{hint}</span>
        </span>
      </label>
      {children && <div className="mt-3 ps-7">{children}</div>}
    </div>
  );
}

/** What a refusal from the server means here, in the reader's language. */
function refusalText(e: unknown, row: ShortfallDetail, t: (key: TranslationKey, vars?: Record<string, string | number>) => string, locale: 'en' | 'ar') {
  if (!(e instanceof ApiError)) return (e as Error).message;
  const details = (e.details ?? {}) as { reason?: string; year?: number; effectiveMonth?: string };
  if (e.code === 'SHORTFALL_ALREADY_DECIDED') return t('shortfall.changed');
  if (e.code === 'SHORTFALL_LEAVE_UNAVAILABLE' && details.reason) {
    return t(`shortfall.leaveProblem.${details.reason}` as TranslationKey, { year: String(details.year ?? row.options.leave.year) });
  }
  if (e.code === 'PAYROLL_PERIOD_LOCKED' && details.effectiveMonth) {
    const [y, m] = details.effectiveMonth.split('-').map(Number);
    return t('shortfall.monthSignedOff', { month: formatMonth(y, m, locale) });
  }
  return e.message;
}

/**
 * Route: `/violations/short-days/:id/decide` — an administrator decides a day more
 * than an hour short: a whole percentage of one day's pay (1–200), with what it
 * comes to, or one day of annual leave instead and nothing from the salary (CR-004
 * §3.2) — or excuses it, nothing taken, with a reason that is then required
 * (operator, 2026-10-28). One of the three, confirmed before it is posted.
 */
export function ShortfallDecidePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<ShortfallDecision | ''>('');
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const { data: row, isLoading, error, refetch } = useQuery({
    queryKey: ['shortfalls', 'one', id],
    queryFn: () => api.get<ShortfallDetail>(`/attendance-shortfalls/${id}`),
  });

  const decide = useMutation({
    mutationFn: () =>
      api.post<ShortfallDetail>(`/attendance-shortfalls/${id}/resolve`, {
        decision,
        ...(decision === 'SALARY_PERCENTAGE' ? { percent: Number(percent) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        version: row!.version,
      }),
    onSuccess: (decided) => {
      toast.success(t('shortfall.decidedToast', { name: localized(decided.user) }));
      void queryClient.invalidateQueries({ queryKey: ['shortfalls'] });
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      navigate(`/violations/short-days/${id}`, { replace: true });
    },
    onError: (e) => {
      toast.error(refusalText(e, row!, t, locale));
      setConfirmed(false);
      void refetch();
    },
  });

  const max = row?.options.maxPercent ?? 200;
  const leave = row?.options.leave;
  const month = row ? row.options.payroll.effectiveMonth.split('-').map(Number) : [0, 0];
  const salaryClosed = !!row?.options.payroll.signedOff;
  const percentValid = percentOk(percent, max);
  const excuse = decision === 'EXCUSED';
  const reasonValid = excuseReasonOk(note);
  const ready =
    !!row?.can.resolve &&
    confirmed &&
    ((decision === 'SALARY_PERCENTAGE' && percentValid && !salaryClosed) ||
      (decision === 'LEAVE_ONE_DAY' && !leave?.problem) ||
      (excuse && reasonValid));
  const rate = row?.options.salary?.dailyRate ?? null;

  return (
    <FormPage
      title={t('shortfall.decideTitle')}
      subtitle={row ? `${localized(row.user)} · ${formatWorkDate(row.workDate, locale)}` : undefined}
      backTo={`/violations/short-days/${id}`}
      backLabel={t('shortfall.title')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={row && <DaySummary row={row} />}
      submitLabel={t('shortfall.submit')}
      onSubmit={() => decide.mutate()}
      submitting={decide.isPending}
      disabled={!ready}
    >
      {row && !row.can.resolve ? (
        <p className="text-body-sm text-muted">
          {row.status === 'PENDING_ADMIN_REVIEW' ? t('shortfall.ownDay') : <ShortfallDecisionText row={row} />}
        </p>
      ) : (
        row && (
          <div className="space-y-4">
            <p className="text-body-sm font-medium text-[var(--text-secondary)]">{t('shortfall.choose')}</p>
            <Choice
              checked={decision === 'SALARY_PERCENTAGE'}
              disabled={salaryClosed}
              onSelect={() => {
                setDecision('SALARY_PERCENTAGE');
                setConfirmed(false);
              }}
              title={t('shortfall.salaryOption')}
              hint={salaryClosed ? t('shortfall.monthSignedOff', { month: formatMonth(month[0], month[1], locale) }) : t('shortfall.salaryOptionHint')}
            >
              {decision === 'SALARY_PERCENTAGE' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2" role="group" aria-label={t('shortfall.percent')}>
                    {PRESETS.filter((p) => p <= max).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          setPercent(String(p));
                          setConfirmed(false);
                        }}
                        className={clsx(
                          'rounded-pill border px-3 py-1 text-body-sm tabular transition-colors duration-fast',
                          percent === String(p) ? 'border-primary bg-primary text-white' : 'hairline hover:border-primary/60',
                        )}
                      >
                        <bdi dir="ltr">{p}%</bdi>
                      </button>
                    ))}
                  </div>
                  <Field
                    label={t('shortfall.percent')}
                    required
                    error={percent && !percentValid ? t('shortfall.percentHint', { max }) : null}
                    hint={t('shortfall.percentHint', { max })}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={max}
                        step={1}
                        value={percent}
                        onChange={(e) => {
                          setPercent(e.target.value);
                          setConfirmed(false);
                        }}
                        className="!w-32 tabular"
                        dir="ltr"
                      />
                      <span className="text-body-sm text-muted">%</span>
                    </div>
                  </Field>
                  {percentValid && (
                    <p className="text-body-sm">
                      <span className="font-medium tabular">{t('shortfall.equivalent', { days: formatDays(Number(percent) / 100, locale) })}</span>
                      <span className="mt-0.5 block text-caption text-muted">
                        {rate != null && row.options.salary
                          ? t('shortfall.amountPreview', {
                              amount: formatMoney(percentAmount(Number(percent), rate), row.options.salary.currency, locale),
                            })
                          : t('shortfall.noSalaryPreview')}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </Choice>
            <Choice
              checked={decision === 'LEAVE_ONE_DAY'}
              disabled={!!leave?.problem}
              onSelect={() => {
                setDecision('LEAVE_ONE_DAY');
                setConfirmed(false);
              }}
              title={t('shortfall.leaveOption')}
              hint={
                leave?.problem
                  ? t(`shortfall.leaveProblem.${leave.problem}` as TranslationKey, { year: String(leave.year) })
                  : t('shortfall.leaveOptionHint')
              }
            >
              {leave && !leave.problem && leave.remaining != null && (
                <p className="text-caption text-muted tabular">
                  {t('shortfall.leaveBalance', { year: String(leave.year), remaining: formatNumber(leave.remaining, locale) })}
                  {leave.pendingDays > 0 && ` · ${t('shortfall.leavePending', { n: formatNumber(leave.pendingDays, locale) })}`}
                </p>
              )}
            </Choice>
            <Choice
              checked={excuse}
              onSelect={() => {
                setDecision('EXCUSED');
                setConfirmed(false);
              }}
              title={t('shortfall.excuseOption')}
              hint={t('shortfall.excuseOptionHint')}
            />

            {/* For an excuse the note is its reason, and required; otherwise it is optional. */}
            <Field
              label={excuse ? t('shortfall.excuseReason') : t('shortfall.note')}
              required={excuse}
              hint={excuse ? t('shortfall.excuseReasonHint') : t('shortfall.noteHint')}
              error={excuse && note.trim() !== '' && !reasonValid ? t('shortfall.excuseReasonHint') : null}
            >
              <Textarea
                value={note}
                maxLength={1000}
                onChange={(e) => {
                  setNote(e.target.value);
                  if (excuse) setConfirmed(false);
                }}
                dir="auto"
              />
            </Field>

            {decision && (
              <Checkbox
                checked={confirmed}
                disabled={decision === 'SALARY_PERCENTAGE' ? !percentValid : excuse ? !reasonValid : !!leave?.problem}
                onChange={(e) => setConfirmed(e.target.checked)}
                label={
                  decision === 'SALARY_PERCENTAGE'
                    ? t('shortfall.confirmSalary', {
                        percent: percentValid ? Number(percent) : '…',
                        name: localized(row.user),
                        date: formatWorkDate(row.workDate, locale),
                      })
                    : excuse
                      ? t('shortfall.confirmExcuse', { name: localized(row.user), date: formatWorkDate(row.workDate, locale) })
                      : t('shortfall.confirmLeave', { name: localized(row.user), date: formatWorkDate(row.workDate, locale) })
                }
              />
            )}
          </div>
        )
      )}
    </FormPage>
  );
}

/**
 * Route: `/violations/short-days/:id/reverse` — an administrator undoes a decision,
 * with a reason kept in the history: the deduction is cancelled (refused once a
 * payroll took it), the day of leave given back or the excuse withdrawn, and the day
 * waits again.
 */
export function ShortfallReversePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const { data: row, isLoading, error, refetch } = useQuery({
    queryKey: ['shortfalls', 'one', id],
    queryFn: () => api.get<ShortfallDetail>(`/attendance-shortfalls/${id}`),
  });

  const reverse = useMutation({
    mutationFn: () => api.post<ShortfallDetail>(`/attendance-shortfalls/${id}/reverse`, { reason: reason.trim(), version: row!.version }),
    onSuccess: () => {
      toast.success(t('shortfall.reversedToast'));
      void queryClient.invalidateQueries({ queryKey: ['shortfalls'] });
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      navigate(`/violations/short-days/${id}`, { replace: true });
    },
    onError: (e) => {
      toast.error(refusalText(e, row!, t, locale));
      void refetch();
    },
  });

  return (
    <FormPage
      title={t('shortfall.reverseTitle')}
      subtitle={row ? `${localized(row.user)} · ${formatWorkDate(row.workDate, locale)}` : undefined}
      backTo={`/violations/short-days/${id}`}
      backLabel={t('shortfall.title')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={
        row && (
          <>
            <DaySummary row={row} />
            <p className="mt-3 text-body-sm">
              <span className="font-medium">
                <ShortfallDecisionText row={row} />
              </span>
              <span className="mt-1 block text-muted">
                {row.status === 'RESOLVED_LEAVE'
                  ? t('shortfall.reverseLeaveNote')
                  : row.status === 'RESOLVED_EXCUSED'
                    ? t('shortfall.reverseExcuseNote')
                    : t('shortfall.reverseSalaryNote')}{' '}
                {t('shortfall.reverseThen')}
              </span>
            </p>
          </>
        )
      }
      submitLabel={t('shortfall.reverse')}
      onSubmit={() => reverse.mutate()}
      submitting={reverse.isPending}
      disabled={!row?.can.reverse || !reason.trim()}
      destructive
    >
      <Field label={t('shortfall.reverseReason')} required hint={t('shortfall.reverseHint')}>
        <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
    </FormPage>
  );
}
